import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ACCOUNT_REFUSAL_EVENT } from '../../utils/fetchInterceptor';
import CloseButton from './CloseButton';

// The app-wide line a restricted, suspended or terminated person sees (policy
// §4; Kurtis 2026-10-09 Q7). The state comes from /api/auth/me
// (`accountState`, `accountStateUntil`); the statement of reasons itself is in
// their notices. When a write is refused, the server's own words replace the
// line for a while — whichever service made the call — via the
// fetch announcer's ACCOUNT_REFUSAL_EVENT, so a refusal never reads as a generic
// error. Not dismissible while the state lasts: it is what explains why
// buttons fail. It links to Account Status (arc 4), where each decision can be
// appealed, except on that page itself.

const REFUSAL_SHOWN_MS = 8000;

/** SQLite UTC datetime text → a local date in the reader's language. */
function formatUntil(until, language) {
  if (!until) return null;
  const s = String(until);
  const d = new Date(/[zZ]$/.test(s) ? s : `${s.replace(' ', 'T')}Z`);
  if (Number.isNaN(d.getTime())) return null;
  try {
    return d.toLocaleDateString(language || undefined, { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return d.toLocaleDateString();
  }
}

function AccountStateBanner() {
  const { currentUser } = useAuth();
  const { t, i18n } = useTranslation('auth');
  const location = useLocation();
  const [refusal, setRefusal] = useState(null);

  useEffect(() => {
    let timer = null;
    const onRefusal = (e) => {
      const message = e?.detail?.message;
      if (!message) return;
      setRefusal(message);
      clearTimeout(timer);
      timer = setTimeout(() => setRefusal(null), REFUSAL_SHOWN_MS);
    };
    window.addEventListener(ACCOUNT_REFUSAL_EVENT, onRefusal);
    return () => {
      window.removeEventListener(ACCOUNT_REFUSAL_EVENT, onRefusal);
      clearTimeout(timer);
    };
  }, []);

  if (!currentUser) return null;
  const state = currentUser.accountState;
  const date = formatUntil(currentUser.accountStateUntil, i18n.language);
  const line = state === 'restricted' ? t('accountBanner.restricted', { date })
    : state === 'suspended' ? t('accountBanner.suspended', { date })
      : state === 'terminated' ? t('accountBanner.terminated')
        : null;
  if (!line && !refusal) return null;
  const onAccountStatus = location.pathname === '/settings' && new URLSearchParams(location.search).get('tab') === 'account';

  return (
    <div className="bg-warning-light border-b border-warning/20 px-4" role="status" aria-live="polite">
      <div className="max-w-5xl mx-auto flex items-center gap-2 text-sm min-h-[40px] py-1.5">
        <p className="flex-1 min-w-0 text-warning-dark">
          {refusal || line}
          {line && !onAccountStatus && (
            <>
              {' '}
              <Link to="/settings?tab=account" className="font-medium underline underline-offset-2 whitespace-nowrap">
                {t('accountBanner.seeStatus')}
              </Link>
            </>
          )}
        </p>
        {refusal && (
          <CloseButton
            variant="plain"
            size="sm"
            onClick={() => setRefusal(null)}
            ariaLabel={t('accountBanner.dismiss')}
            className="-mr-2 -my-1 text-warning-dark hover:text-warning-dark"
          />
        )}
      </div>
    </div>
  );
}

export default AccountStateBanner;
